import { useStableCallback } from "@prismicio/editor-support/React"
import {
  isBooleanContent,
  isColorContent,
  isDateContent,
  isEmbedContent,
  isGeoPointContent,
  isImageContent,
  isIntegrationFieldContent,
  isNumberContent,
  isRichTextContent,
  isSelectContent,
  isTableContent,
  isTextContent,
  isTimestampContent,
  NestableContent,
} from "@prismicio/types-internal/lib/content"
import type { NestableWidget } from "@prismicio/types-internal/lib/customtypes"
import { useMemo } from "react"

import { BooleanField } from "./fields/BooleanField"
import { ColorField } from "./fields/ColorField"
import { DateField } from "./fields/DateField"
import { EmbedField } from "./fields/EmbedField"
import { GeoPointField } from "./fields/GeoPointField"
import { ImageField } from "./fields/ImageField"
import { IntegrationField } from "./fields/IntegrationField"
import { KeyTextField } from "./fields/KeyTextField"
import { LinkOrRepeatableLinkField } from "./fields/LinkField/LinkOrRepeatableLinkField"
import { NumberField } from "./fields/NumberField"
import { RichTextField } from "./fields/RichTextField"
import { SelectField } from "./fields/SelectField"
import { TableField } from "./fields/TableField"
import { TimestampField } from "./fields/TimestampField"

interface Props {
  content: NestableContent | undefined
  id: string
  nestableWidget: NestableWidget
  onContentChange: (content: NestableContent | undefined) => void
  readOnly: boolean
}

export function NestableWidgetSwitch(props: Props) {
  const { id, readOnly, content, nestableWidget } = props

  const onContentChange = useStableCallback(props.onContentChange)

  return useMemo(() => {
    const fieldProps = { id, readOnly, onContentChange }

    if (nestableWidget.type === "Boolean")
      return (
        <BooleanField
          {...fieldProps}
          field={nestableWidget}
          content={isBooleanContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "Date")
      return (
        <DateField
          {...fieldProps}
          field={nestableWidget}
          content={isDateContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "Timestamp")
      return (
        <TimestampField
          {...fieldProps}
          field={nestableWidget}
          content={isTimestampContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "Image")
      return (
        <ImageField
          {...fieldProps}
          field={nestableWidget}
          content={isImageContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "Link") {
      return (
        <LinkOrRepeatableLinkField
          {...fieldProps}
          field={nestableWidget}
          content={content}
        />
      )
    } else if (nestableWidget.type === "Number")
      return (
        <NumberField
          {...fieldProps}
          field={nestableWidget}
          content={isNumberContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "StructuredText")
      return (
        <RichTextField
          {...fieldProps}
          field={nestableWidget}
          content={isRichTextContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "Select")
      return (
        <SelectField
          {...fieldProps}
          field={nestableWidget}
          content={isSelectContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "Text")
      return (
        <KeyTextField
          {...fieldProps}
          field={nestableWidget}
          content={isTextContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "Embed")
      return (
        <EmbedField
          {...fieldProps}
          field={nestableWidget}
          content={isEmbedContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "Color")
      return (
        <ColorField
          {...fieldProps}
          field={nestableWidget}
          content={isColorContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "GeoPoint")
      return (
        <GeoPointField
          {...fieldProps}
          field={nestableWidget}
          content={isGeoPointContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "IntegrationFields")
      return (
        <IntegrationField
          {...fieldProps}
          field={nestableWidget}
          content={isIntegrationFieldContent(content) ? content : undefined}
        />
      )
    else if (nestableWidget.type === "Table")
      return (
        <TableField
          {...fieldProps}
          field={nestableWidget}
          content={isTableContent(content) ? content : undefined}
        />
      )
    else return null
  }, [content, id, nestableWidget, onContentChange, readOnly])
}
